import type { TenantEdge } from '@/features/tenants/api/tenants.graphql';
import { expiryAlertLabel, type TenantTab } from '@/features/tenants/tenant-labels';

import type { ChurnRisk, TenantHealthScore } from './api/metrics.graphql';

/** OS-1724 — nombre de cada riesgo; los tres últimos no tienen puntaje (no son cero). */
export const RISK_LABELS: Readonly<Record<ChurnRisk, string>> = {
  low: 'Riesgo bajo',
  medium: 'Riesgo medio',
  high: 'Riesgo alto',
  new: 'Nuevo (en gracia)',
  no_data: 'Sin datos de uso',
  not_operating: 'No opera',
};

/** Definición de la salud, visible en el dashboard y como ayuda del indicador. */
export const HEALTH_DEFINITION =
  'Salud de 0 a 100 con el uso de los últimos 30 días: adopción (días con al menos un usuario ÷ ' +
  'días evaluados × 60) + actividad (DAU promedio ÷ 10 × 40, máximo 40). Los días evaluados ' +
  'cuentan desde el alta, con tope de 30. Riesgo alto bajo 30, medio bajo 60. Sin puntaje: ' +
  'tenants con menos de 7 días (gracia), sin registros de uso (no es cero) o suspendidos y ' +
  'cancelados. Tendencia: DAU de 7 días frente al de 30.';

export const TREND_LABELS: Readonly<Record<'up' | 'down' | 'flat', string>> = {
  up: 'Uso en alza',
  down: 'Uso en baja',
  flat: 'Uso estable',
};

const DAY_MS = 24 * 60 * 60 * 1000;
export const INACTIVITY_DAYS = 7;

/**
 * Sin actividad en 7 días. null cuando no se puede saber (sin registros de uso o sin permiso de
 * reportes): no se confunde con "inactivo".
 */
export function inactiveForAWeek(health: TenantHealthScore | undefined, now: Date): boolean | null {
  if (!health || !health.usageAvailable) {
    return null;
  }
  if (!health.lastActivity) {
    return true;
  }
  return now.getTime() - new Date(health.lastActivity).getTime() > INACTIVITY_DAYS * DAY_MS;
}

export interface TenantAlert {
  label: string;
  /** Pestaña de la ficha que resuelve la alerta (queda en la URL). */
  tab: TenantTab;
  tone: 'danger' | 'warning';
}

/** Alerta principal de un tenant para la tarjeta, por prioridad. */
export function mainAlert(tenant: TenantEdge, health: TenantHealthScore | undefined): TenantAlert | null {
  if (tenant.expiryAlert && tenant.expiryAlert.level !== 'NONE') {
    return {
      label: `Piloto: ${expiryAlertLabel(tenant.expiryAlert).toLowerCase()}`,
      tab: 'summary',
      tone: tenant.expiryAlert.level === 'DAYS_7' ? 'warning' : 'danger',
    };
  }
  if (tenant.planStatus === 'past_due') {
    return { label: 'En mora', tab: 'summary', tone: 'danger' };
  }
  if (health?.usageDrop) {
    return { label: 'Caída de uso', tab: 'summary', tone: 'warning' };
  }
  if (tenant.onboarding?.stalled) {
    return { label: 'Onboarding estancado', tab: 'general', tone: 'warning' };
  }
  return null;
}
